import { motion } from "framer-motion";
import { Compass, MoonStar, ShieldCheck } from "lucide-react";

const TOOLS = [
  {
    icon: Compass,
    title: "A proven framework for mastering your mindset and results",
    tag: "LifeWorks Life Mastery System",
  },
  {
    icon: MoonStar,
    title: "Neuroscience-based recovery that gives you deep rest in minutes",
    tag: "NuCalm",
  },
  {
    icon: ShieldCheck,
    title: "Affordable access to top-rated law firms for life's unexpected moments",
    tag: "LegalShield",
  },
];

export default function StartHere() {
  return (
    <section id="start-here" data-testid="start-here-section" className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <h2
            data-testid="start-here-heading"
            className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
          >
            Why People Follow This Page
          </h2>
          <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
            For years I've been obsessed with one question: why do some people seem to have energy,
            clarity, and confidence while others are running on empty? The answer isn't hustle. It's
            a system.
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
            I've spent my career learning and teaching that system, and I've brought together three
            tools I personally use and stand behind:
          </p>
        </motion.div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {TOOLS.map(({ icon: Icon, title, tag }, i) => (
            <motion.div
              key={tag}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              data-testid={`start-here-tool-${i + 1}`}
              className="rounded-2xl border border-slate-200 bg-[#FBF9F4] p-6 transition-all duration-200 hover:-translate-y-1 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-900/5"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-700/10 text-blue-700">
                <Icon className="h-5 w-5" />
              </span>
              <p className="mt-4 text-sm font-semibold leading-relaxed text-slate-800">{title}</p>
              <p className="mt-2 text-xs font-bold uppercase tracking-wide text-blue-600">{tag}</p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          data-testid="start-here-mission"
          className="mx-auto mt-14 max-w-2xl text-center font-display text-xl font-semibold leading-relaxed text-slate-900 sm:text-2xl"
        >
          Three tools. One mission:{" "}
          <span className="text-blue-700">
            helping you perform at your best, grow on purpose, and protect everything you're
            building.
          </span>
        </motion.p>
      </div>
    </section>
  );
}
